// src/components/Admin/SellerManagement/LeadHistory/LeadReturnStatsTable.jsx
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { Badge } from "../../../../components/ui/badge";
import { getLeadReturnAnalytics } from '../../../../services/AdminApis';
import { RefreshCcw, AlertTriangle } from 'lucide-react';

const LeadReturnStatsTable = ({ sellerProfileId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            if (!sellerProfileId) return;
            try {
                const result = await getLeadReturnAnalytics(sellerProfileId);
                setData(result);
            } catch (error) {
                console.error("Failed to load return stats", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [sellerProfileId]);

    if (loading) return <div className="p-4 text-xs text-muted-foreground">Loading analytics...</div>;
    if (!data) return null;

    // Helper for visual cues on high return rates
    const getRateBadge = (rate) => {
        let colorClass = "bg-green-50 text-green-700 border-green-200";
        if (rate > 20) colorClass = "bg-red-50 text-red-700 border-red-200";
        else if (rate > 10) colorClass = "bg-yellow-50 text-yellow-700 border-yellow-200";
        
        return <Badge variant="outline" className={`${colorClass} ml-2 border`}>{rate}%</Badge>;
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Left Card: 30-Day vs Lifetime Stats */}
            <Card className="shadow-sm">
                <CardHeader className="pb-3 pt-4">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <RefreshCcw className="w-4 h-4 text-muted-foreground" /> Return Performance
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50 hover:bg-slate-50">
                                <TableHead className="w-[120px] h-9">Timeframe</TableHead>
                                <TableHead className="text-center h-9">Purchased</TableHead>
                                <TableHead className="text-center h-9">Returned</TableHead>
                                <TableHead className="text-right h-9 pr-6">Rate</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow>
                                <TableCell className="font-medium text-muted-foreground">Last 30 Days</TableCell>
                                <TableCell className="text-center">{data.last_30_days.purchased}</TableCell>
                                <TableCell className="text-center">{data.last_30_days.returned}</TableCell>
                                <TableCell className="text-right pr-4">
                                    {getRateBadge(data.last_30_days.return_rate_percentage)}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-medium text-muted-foreground">Lifetime (365d)</TableCell>
                                <TableCell className="text-center">{data.last_365_days.purchased}</TableCell>
                                <TableCell className="text-center">{data.last_365_days.returned}</TableCell>
                                <TableCell className="text-right pr-4">
                                    {getRateBadge(data.last_365_days.return_rate_percentage)}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Right Card: Reasons Breakdown */}
            <Card className="shadow-sm">
                <CardHeader className="pb-3 pt-4">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-muted-foreground" /> Return Reasons
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 max-h-[160px] overflow-auto">
                    {data.return_reasons.length === 0 ? (
                        <div className="text-xs text-muted-foreground italic p-6 text-center">No returns recorded yet.</div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-slate-50 hover:bg-slate-50">
                                    <TableHead className="h-9">Reason</TableHead>
                                    <TableHead className="text-right h-9 pr-6">Count</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.return_reasons.map((item, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell className="text-xs font-medium py-2">{item.reason}</TableCell>
                                        <TableCell className="text-right text-xs py-2 pr-6">{item.count}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default LeadReturnStatsTable;